import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  DEFAULT_LOCALE,
  type Dictionary,
  getDictionary,
  type Locale,
  readLocale,
  writeLocale,
} from './index.js';

interface I18nContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  dict: Dictionary;
}

const I18nContext = createContext<I18nContextValue>({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  dict: getDictionary(DEFAULT_LOCALE),
});

interface I18nProviderProps {
  children: ReactNode;
  /** Override default. Useful for tests / SSR. */
  initial?: Locale;
}

export function I18nProvider({ children, initial }: I18nProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(() => initial ?? readLocale());

  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((l: Locale) => {
    writeLocale(l);
    setLocaleState(l);
  }, []);

  const value = useMemo<I18nContextValue>(
    () => ({ locale, setLocale, dict: getDictionary(locale) }),
    [locale, setLocale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  return useContext(I18nContext);
}

/** Direct access to the dictionary. */
export function useDict(): Dictionary {
  return useContext(I18nContext).dict;
}
